// src/api.js
import axios from 'axios';

// Configuration de l'URL de base de l'API
// En production, utilise l'URL actuelle, en développement utilise localhost:4001
export const API_URL = process.env.REACT_APP_API_URL || 
    (process.env.NODE_ENV === 'production' ? window.location.origin : 'http://localhost:4001');

// Création d'une instance axios avec des configurations par défaut
const api = axios.create({
    baseURL: `${API_URL}/api`,
    timeout: 30000, // Augmentation du timeout à 30 secondes pour les serveurs sous charge
    headers: {
        'Content-Type': 'application/json',
    }
});

// Intercepteur pour ajouter le token d'authentification aux requêtes
api.interceptors.request.use(
    config => {
        const token = localStorage.getItem('DISCORD_BOT_TOKEN');
        if (token) {
            config.headers['Authorization'] = `Bearer ${token}`;
        }
        return config;
    },
    error => {
        return Promise.reject(error);
    }
);

// Intercepteur pour gérer les réponses et les erreurs
api.interceptors.response.use(
    response => {
        return response;
    },
    error => {
        // Gestion des erreurs de réseau ou de timeout
        if (error.code === 'ECONNABORTED' || !error.response) {
            console.error('Erreur réseau ou timeout:', error.message);
        }
        
        // Gestion du rate limiting (429)
        if (error.response && error.response.status === 429) {
            const retryAfter = error.response.data?.retryAfter || 60;
            console.warn(`⏳ Rate limit atteint. Attendre ${retryAfter} secondes.`);
            // Vous pouvez ajouter une notification à l'utilisateur ici
        }
        
        // Si le serveur répond avec une erreur 401 (non autorisé), déconnecter l'utilisateur
        if (error.response && error.response.status === 401) {
            localStorage.removeItem('DISCORD_BOT_TOKEN');
            window.location.href = '/';
        }
        
        return Promise.reject(error);
    }
);

// 🆕 Fonctions API pour les événements
export const eventsAPI = {
    // Récupérer tous les événements d'un serveur
    getEvents: (serverId, params = {}) => 
        api.get(`/events/${serverId}`, { params }),
    
    // Récupérer les événements actifs
    getActiveEvents: (serverId, type = null) => 
        api.get(`/events/${serverId}/active`, { params: type ? { type } : {} }),
    
    // Récupérer un événement spécifique
    getEvent: (serverId, eventId) => 
        api.get(`/events/${serverId}/${eventId}`),
    
    // Créer un nouvel événement
    createEvent: (serverId, eventData) => 
        api.post(`/events/${serverId}`, eventData),
    
    // Mettre à jour un événement
    updateEvent: (serverId, eventId, eventData) => 
        api.put(`/events/${serverId}/${eventId}`, eventData),
    
    // Supprimer un événement
    deleteEvent: (serverId, eventId) => 
        api.delete(`/events/${serverId}/${eventId}`),
      // Activer/désactiver un événement
    toggleEvent: (serverId, eventId) => 
        api.patch(`/events/${serverId}/${eventId}/toggle`),
    
    // Récupérer les statistiques des événements
    getEventStats: (serverId) => 
        api.get(`/events/${serverId}/stats`)
};

// 🆕 Fonctions API pour les saisons avec événements
export const seasonsAPI = {
    // Recalculer une saison avec les événements
    recalculateWithEvents: (serverId, saisonId) => 
        api.post(`/servers/${serverId}/saisons/${saisonId}/recalculate-with-events`),
    
    // Obtenir les événements actifs d'une saison
    getSeasonActiveEvents: (serverId, saisonId) => 
        api.get(`/servers/${serverId}/saisons/${saisonId}/active-events`),
    
    // Obtenir les détails de combat d'un joueur avec les dates
    getPlayerCombatDetails: (serverId, saisonId, playerId) => 
        api.get(`/servers/${serverId}/saisons/${saisonId}/player/${playerId}/combat-details`)
};

// 🆕 Fonctions API pour les configurations de channels
export const channelConfigsAPI = {
    getConfigs: (serverId) =>
        api.get(`/channel-configs/${serverId}`),
    getConfig: (configId) =>
        api.get(`/channel-config/${configId}`),
    createConfig: (data) =>
        api.post('/channel-config', data),
    updateConfig: (configId, data) =>
        api.put(`/channel-config/${configId}`, data),
    deleteConfig: (configId) =>
        api.delete(`/channel-config/${configId}`),
    setDefault: (configId) =>
        api.post(`/channel-config/${configId}/set-default`),
    migrate: (serverId) =>
        api.post(`/channel-config/migrate/${serverId}`)
};

export default api;
